import React, { useState } from 'react';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { ordersApi, scansApi, employeesApi, bundlesApi } from '../services/api';
import { useEmployee } from '../contexts/EmployeeContext';
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';

interface OrderItem {
  id: string;
  title: string;
  sku?: string;
  quantity: number;
  scanned_quantity?: number;
  verified?: boolean;
  is_bundle_component?: boolean;
  bundle_parent_sku?: string;
  original_title?: string;
  barcode?: string;
}

export const PackingStation: React.FC = () => {
  const { employee } = useEmployee();
  const [currentOrder, setCurrentOrder] = useState<any>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [scanStep, setScanStep] = useState<'order' | 'items'>('order');
  const [lastScan, setLastScan] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [infoMessage, setInfoMessage] = useState<string>('');
  const [scannerFocusKey, setScannerFocusKey] = useState<number>(0);
  const [showBagConfirm, setShowBagConfirm] = useState<boolean>(false);
  const [stationName] = useState<string>(
    localStorage.getItem('station_name') || `Station-${Math.random().toString(36).substr(2, 6)}`
  );

  const logActivity = async (orderId: string, actionType: string, description: string, itemId?: string, quantity?: number) => {
    if (!employee) return;
    try {
      await employeesApi.logActivity({
        employee_id: employee.id,
        order_id: orderId,
        action_type: actionType,
        description,
        item_id: itemId,
        quantity,
      });
    } catch (error) {
      console.error('Failed to log activity:', error);
    }
  };

  const formatOrderNumber = (orderNumber: string | number | undefined | null) => {
    const raw = (orderNumber || '').toString().trim();
    if (!raw) return '';
    if (raw.toUpperCase().startsWith('PSCS#')) return raw.replace(/^PSCS/i, '');
    if (raw.startsWith('#') || raw.toUpperCase().startsWith('PSCS')) return raw;
    if (/^[0-9]+$/.test(raw)) return `#PSCS${raw}`;
    return `#${raw}`;
  };

  const handleBarcodeScan = async (barcode: string) => {
    setLastScan(barcode);
    setError('');
    setInfoMessage('');

    if (scanStep === 'order') {
      await loadOrderByBarcode(barcode);
    } else {
      await verifyItem(barcode);
    }
  };

  const loadOrderByBarcode = async (barcode: string) => {
    try {
      const orderNumber = barcode.trim();

      let order: any;
      try {
        const lookupResult = await ordersApi.lookup(orderNumber);
        order = lookupResult.order;
      } catch (lookupErr: any) {
        if (lookupErr?.response?.status === 404) {
          setError(`Order not found: ${orderNumber}`);
        } else {
          setError(`Failed to look up order: ${orderNumber}`);
        }
        return;
      }

      if (order.status !== 'awaiting_shipment') {
        setError(`Order ${orderNumber} has status "${order.status}" - must be "Awaiting Shipment"`);
        return;
      }

      const orderDetails = await ordersApi.get(order.id);
      setCurrentOrder(orderDetails.order);

      if (orderDetails.order?.order_type === 'pickup') {
        setError(`This is a LOCAL PICKUP order - use "Mark Ready for Pickup" from Orders page.`);
      }

      logActivity(order.id, 'order_scanned', `Scanned order #${order.order_number} at packing station`);

      let existingScans: any = {};
      try {
        const scansData = await scansApi.getOrderScans(order.id);
        scansData.scans.forEach((scan: any) => {
          // Only count packing scans.
          // Legacy compatibility: older builds stored department as stationName (e.g., "Station-abc123").
          const dept = (scan.department || '').toString();
          const isPackingScan = dept === 'packing' || dept.startsWith('Station-');
          if (isPackingScan) {
            existingScans[scan.order_item_id] = (existingScans[scan.order_item_id] || 0) + 1;
          }
        });
      } catch (error) {
        console.error('Failed to load existing scans:', error);
      }

      let itemsToVerify = orderDetails.items.filter((item: any) => item.requires_packing_verification === true);

      try {
        const expandResult = await bundlesApi.expandForPacking(itemsToVerify);
        if (expandResult.expanded) {
          itemsToVerify = expandResult.items;
        }
      } catch (bundleError) {
        console.error('Failed to expand bundles:', bundleError);
      }

      const itemsWithScan = itemsToVerify.map((item: any) => ({
        ...item,
        scanned_quantity: existingScans[item.id] || 0,
        verified: (existingScans[item.id] || 0) >= item.quantity,
      }));
      setItems(itemsWithScan);
      setScanStep('items');

      if (itemsWithScan.length === 0) {
        setInfoMessage('No items on this order require packing verification. You can proceed to the next order.');
      }

      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTUIGGS57OihUBELTKXh8bllHgU2jdXxxHkpBSh+zPLaizsIHGi+7OynWBIKU6zm8LJcHAU7k9n0xn4qBCeAy/DajDoJHm/A7u2nWRQKVLDo8LVeHQU+ltv1yIAtBSqDzvLaiTsIF2m98OqiVBEJTqvl8bVkHwU5kdny0H8rBSiBzvPajDwJH3HB7uynWhQLVbHp8rdgHgVAmtz2yoEuBSuEz/PcijwKGGu+8eujVRIJUK3m8rZmHwU7lNr0z4IsBCmCzvLbjTwKIG/D7+2oWxULVrLq87dhHwVBnN33y4IvBSyF0PPdjT0KGWzA8u2kVhMKUbDn87doIAU9lt3z0YMtBCqDz/PcjjwLInHE8O+pXBYMV7Tr9LhjIAVDnt73zIMwBi2H0fTejj4LGm7C8+6lVxQLUrHo9LlpIQU+mN701YQu');
      audio.play().catch(() => {});
    } catch (err: any) {
      setError(`Failed to load order: ${err.message}`);
    }
  };

  const verifyItem = async (barcode: string) => {
    const itemIndex = items.findIndex(
      (item) =>
        item.sku === barcode ||
        item.barcode === barcode ||
        (item as any).variant_sku === barcode ||
        (item as any).variant_barcode === barcode ||
        item.title.toLowerCase().includes(barcode.toLowerCase())
    );

    if (itemIndex === -1) {
      setError(`Item not found: ${barcode}`);
      const audio = new Audio('data:audio/wav;base64,UklGRhIAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0Yf////8AAA==');
      audio.play().catch(() => {});
      return;
    }

    const updatedItems = [...items];
    const item = updatedItems[itemIndex];
    item.scanned_quantity = (item.scanned_quantity || 0) + 1;

    if (item.scanned_quantity >= item.quantity) {
      item.verified = true;
    }

    setItems(updatedItems);

    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTUIGGS57OihUBELTKXh8bllHgU2jdXxxHkpBSh+zPLaizsIHGi+7OynWBIKU6zm8LJcHAU7k9n0xn4qBCeAy/DajDoJHm/A7u2nWRQKVLDo8LVeHQU+ltv1yIAtBSqDzvLaiTsIF2m98OqiVBEJTqvl8bVkHwU5kdny0H8rBSiBzvPajDwJH3HB7uynWhQLVbHp8rdgHgVAmtz2yoEuBSuEz/PcijwKGGu+8eujVRIJUK3m8rZmHwU7lNr0z4IsBCmCzvLbjTwKIG/D7+2oWxULVrLq87dhHwVBnN33y4IvBSyF0PPdjT0KGWzA8u2kVhMKUbDn87doIAU9lt3z0YMtBCqDz/PcjjwLInHE8O+pXBYMV7Tr9LhjIAVDnt73zIMwBi2H0fTejj4LGm7C8+6lVxQLUrHo9LlpIQU+mN701YQu');
    audio.play().catch(() => {});

    try {
      await scansApi.recordScan(currentOrder.id, item.id, 'packing');
      logActivity(currentOrder.id, 'item_packed', `Packed ${item.title} (${item.scanned_quantity}/${item.quantity})`, item.id, 1);
    } catch (error) {
      console.error('Failed to save scan:', error);
    }

    const allVerified = updatedItems.every(i => i.verified);
    if (allVerified) {
      logActivity(currentOrder.id, 'packing_complete', 'All packing items verified');
      setShowBagConfirm(true);
    }
  };

  const handleVerifyItem = async (itemId: string) => {
    const itemIndex = items.findIndex(i => i.id === itemId);
    if (itemIndex === -1) return;
    
    const updatedItems = [...items];
    const item = updatedItems[itemIndex];
    
    // Record scans for remaining quantity
    const scansNeeded = item.quantity - (item.scanned_quantity || 0);
    for (let i = 0; i < scansNeeded; i++) {
      try {
            await scansApi.recordScan(currentOrder.id, item.id, 'packing');
        logActivity(currentOrder.id, 'item_packed', `Packed ${item.title}`, item.id, 1);
      } catch (error) {
        console.error('Failed to save scan:', error);
      }
    }
    
    item.scanned_quantity = item.quantity;
    item.verified = true;
    
    setItems(updatedItems);
    
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTUIGGS57OihUBELTKXh8bllHgU2jdXxxHkpBSh+zPLaizsIHGi+7OynWBIKU6zm8LJcHAU7k9n0xn4qBCeAy/DajDoJHm/A7u2nWRQKVLDo8LVeHQU+ltv1yIAtBSqDzvLaiTsIF2m98OqiVBEJTqvl8bVkHwU5kdny0H8rBSiBzvPajDwJH3HB7uynWhQLVbHp8rdgHgVAmtz2yoEuBSuEz/PcijwKGGu+8eujVRIJUK3m8rZmHwU7lNr0z4IsBCmCzvLbjTwKIG/D7+2oWxULVrLq87dhHwVBnN33y4IvBSyF0PPdjT0KGWzA8u2kVhMKUbDn87doIAU9lt3z0YMtBCqDz/PcjjwLInHE8O+pXBYMV7Tr9LhjIAVDnt73zIMwBi2H0fTejj4LGm7C8+6lVxQLUrHo9LlpIQU+mN701YQu');
    audio.play().catch(() => {});
    
    const allVerified = updatedItems.every(i => i.verified);
    if (allVerified) {
      logActivity(currentOrder.id, 'packing_complete', 'All packing items verified');
      setShowBagConfirm(true);
    }
  };

  const handleClearItem = (itemId: string) => {
    // Note: Scans remain in DB - this just resets local state for re-verification
    setItems(items.map(item => 
      item.id === itemId ? { ...item, scanned_quantity: 0, verified: false } : item
    ));
  };

  const handleVerifyAll = async () => {
    // Save all scans to database
    for (const item of items) {
      if (!item.verified) {
        const scansNeeded = item.quantity - (item.scanned_quantity || 0);
        for (let i = 0; i < scansNeeded; i++) {
          try {
            await scansApi.recordScan(currentOrder.id, item.id, 'packing');
          } catch (error) {
            console.error('Failed to save scan:', error);
          }
        }
      }
    }
    
    setItems(items.map(item => ({ ...item, scanned_quantity: item.quantity, verified: true })));
    logActivity(currentOrder.id, 'packing_complete', 'All packing items verified (bulk)');
    setShowBagConfirm(true);
  };

  const handleClearAll = () => {
    // Note: Scans remain in DB - this just resets local state
    setItems(items.map(item => ({ ...item, scanned_quantity: 0, verified: false })));
  };

  const handleCompleteOrder = () => {
    if (currentOrder) {
      logActivity(currentOrder.id, 'order_packed', `Order packing completed at ${stationName}`);
    }
    setShowBagConfirm(false);
    setCurrentOrder(null);
    setItems([]);
    setScanStep('order');
    setLastScan('');
    setInfoMessage('');
    setScannerFocusKey((k) => k + 1);
  };

  const allItemsVerified = items.length > 0 && items.every(item => item.verified);
  const remainingItems = items.filter(i => !i.verified).length;
  const totalItemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  React.useEffect(() => {
    localStorage.setItem('station_name', stationName);
  }, [stationName]);

  return (
    <div className="absolute inset-0 bg-slate-100 flex flex-col overflow-hidden">
      {/* Header Bar */}
      <div className="bg-slate-800 text-white px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <h1 className="text-lg font-semibold">Packing Station</h1>
            <span className="text-slate-400">|</span>
            <span className="text-sm text-slate-300">{stationName}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="text-sm text-slate-300">Active</span>
          </div>
        </div>
      </div>

      {/* Order Info Bar */}
      {currentOrder && (
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className="text-lg font-semibold text-slate-900">
                  Order: <span className="text-blue-600">{formatOrderNumber(currentOrder.order_number)}</span>
                </span>
                {currentOrder.order_type === 'pickup' && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded">LOCAL PICKUP</span>
                )}
              </div>
              <p className="text-sm text-slate-600 mt-1">
                Ship To: {currentOrder.first_name} {currentOrder.last_name} • {currentOrder.shipping_city}, {currentOrder.shipping_state} {currentOrder.shipping_zip}
              </p>
            </div>
            {allItemsVerified && (
              <button
                onClick={handleCompleteOrder}
                className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
              >
                <CheckIcon className="w-5 h-5" />
                Complete & Next Order
              </button>
            )}
          </div>
        </div>
      )}

      <div className="p-6 flex-1 min-h-0 flex flex-col overflow-hidden relative">
        {/* Scan Input Section */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm mb-4 flex-shrink-0">
          <div className="p-4 border-b border-slate-200">
            <BarcodeScanner
              key={scannerFocusKey}
              onScan={handleBarcodeScan}
              placeholder={scanStep === 'order' ? 'Scan order barcode to start...' : 'Scan or enter SKU/UPC to verify...'}
            />
          </div>

          {/* Feedback */}
          {(lastScan || error) && (
            <div className={`px-4 py-3 flex items-center gap-2 ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {error ? (
                <><XCircleIcon className="w-5 h-5" /> {error}</>
              ) : (
                <><CheckCircleIcon className="w-5 h-5" /> Scanned: <span className="font-mono font-medium">{lastScan}</span></>
              )}
            </div>
          )}

          {infoMessage && !error && (
            <div className="px-4 py-3 flex items-center gap-2 bg-blue-50 text-blue-700 border-t border-blue-100">
              <CheckCircleIcon className="w-5 h-5" /> {infoMessage}
            </div>
          )}
        </div>

        {/* Items List */}
        {currentOrder && items.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col">
            {/* Table Header */}
            <div className="flex-shrink-0 grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wide">
              <div className="col-span-5">Product</div>
              <div className="col-span-2 text-center">Actions</div>
              <div className="col-span-2 text-center">Order Qty.</div>
              <div className="col-span-2 text-center">Scanned</div>
              <div className="col-span-1 text-center">Status</div>
            </div>

            {/* Quick Actions Row */}
            <div className="flex-shrink-0 grid grid-cols-12 gap-4 px-4 py-2 border-b border-slate-200 bg-slate-50/50">
              <div className="col-span-5"></div>
              <div className="col-span-2 flex items-center justify-center gap-3">
                <button onClick={handleVerifyAll} className="text-xs text-blue-600 hover:text-blue-800 font-medium">Verify All</button>
                <span className="text-slate-300">|</span>
                <button onClick={handleClearAll} className="text-xs text-slate-500 hover:text-slate-700 font-medium">Clear All</button>
              </div>
              <div className="col-span-5"></div>
            </div>

            {/* Items - only this scrolls */}
            <div className={`divide-y divide-slate-100 flex-1 min-h-0 overflow-y-auto ${allItemsVerified ? 'pb-28' : ''}`}>
              {items.map((item) => (
                <div
                  key={item.id}
                  className={`grid grid-cols-12 gap-4 px-4 py-4 items-center transition-colors ${
                    item.verified ? 'bg-green-50' : 'hover:bg-slate-50'
                  }`}
                >
                  {/* Product Info */}
                  <div className="col-span-5">
                    {item.is_bundle_component && (
                      <p className="text-xs text-purple-600 font-medium mb-1">
                        📦 Bundle: {item.bundle_parent_sku}
                      </p>
                    )}
                    <p className="font-medium text-slate-900">{item.title}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span><span className="font-medium">SKU:</span> {item.sku || 'N/A'}</span>
                      {(item.barcode || (item as any).variant_barcode) && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-mono">
                          UPC: {item.barcode || (item as any).variant_barcode}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="col-span-2 flex items-center justify-center gap-3 text-sm">
                    <button 
                      onClick={() => handleVerifyItem(item.id)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Verify
                    </button>
                    <span className="text-slate-300">|</span>
                    <button 
                      onClick={() => handleClearItem(item.id)}
                      className="text-slate-500 hover:text-slate-700 font-medium"
                    >
                      Clear
                    </button>
                  </div>

                  {/* Order Qty */}
                  <div className="col-span-2 text-center">
                    <span className="text-3xl font-bold text-slate-800">{item.quantity}</span>
                  </div>

                  {/* Scanned */}
                  <div className="col-span-2 text-center">
                    <span className={`text-3xl font-bold ${
                      item.verified ? 'text-green-600' : 
                      (item.scanned_quantity || 0) > 0 ? 'text-amber-600' : 'text-slate-300'
                    }`}>
                      {item.scanned_quantity || 0}
                    </span>
                  </div>

                  {/* Status */}
                  <div className="col-span-1 flex justify-center">
                    {item.verified ? (
                      <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                        <CheckIcon className="w-5 h-5 text-white" />
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-slate-200"></div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-600">
                The workflow will automatically advance once all items are verified.
              </p>
              <p className="text-sm font-semibold text-slate-900">
                {remainingItems > 0 ? `${remainingItems} Remaining` : 'All Verified'}
              </p>
            </div>

          </div>
        )}

        {/* Complete Banner */}
        {currentOrder && allItemsVerified && (
          <div className="absolute bottom-0 left-6 right-6 z-10 px-6 py-2.5 bg-green-600 text-white text-center rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold mb-1">All Items Verified!</h3>
            <p className="text-green-100 text-sm mb-2">Order {formatOrderNumber(currentOrder.order_number)} is ready for shipping</p>
            <button
              onClick={handleCompleteOrder}
              className="px-6 py-2 bg-white text-green-700 rounded-lg font-semibold hover:bg-green-50"
            >
              Complete & Scan Next Order
            </button>
          </div>
        )}

        {/* No Packing Verification Needed */}
        {currentOrder && items.length === 0 && infoMessage && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Packing Verification Needed</h3>
            <p className="text-slate-600 mb-6">
              All items on order <span className="font-semibold">{formatOrderNumber(currentOrder.order_number)}</span> are ready to move forward without packing scans.
            </p>
            <button
              onClick={handleCompleteOrder}
              className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
            >
              Scan Next Order
            </button>
          </div>
        )}

        {/* No Order State */}
        {!currentOrder && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ArrowPathIcon className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Ready to Scan</h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              Scan an order barcode (e.g., PSCS22750) to load items for verification
            </p>
            <div className="bg-slate-50 rounded-lg p-6 max-w-lg mx-auto text-left">
              <h4 className="font-semibold text-slate-800 mb-3">Workflow</h4>
              <ol className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-slate-800 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  Scan order barcode to load items
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-slate-800 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  Scan each item's SKU/UPC as you pack it
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-slate-800 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  Scan multiple times for quantity &gt; 1
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-slate-800 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  Complete to move to next order
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>

      {/* Bag Confirmation Popup */}
      {showBagConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 mx-4 max-w-sm w-full text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckIcon className="w-9 h-9 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">All Items Scanned</h2>
            <p className="text-slate-600 mb-6">
              Please confirm there are{' '}
              <span className="text-2xl font-bold text-slate-900">{totalItemCount}</span>{' '}
              {totalItemCount === 1 ? 'item' : 'items'} bagged.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleCompleteOrder}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 text-lg"
              >
                Confirm &amp; Complete Order
              </button>
              <button
                onClick={() => setShowBagConfirm(false)}
                className="w-full px-6 py-2 text-slate-500 hover:text-slate-700 text-sm font-medium"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
